import React, { useState } from 'react';
import { Mail, ArrowRight, Loader2, AlertCircle, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuthHook';

interface MagicLinkFormProps {
  onLinkSent?: (sent: boolean) => void;
  next?: string;
}

export function MagicLinkForm({ onLinkSent, next }: MagicLinkFormProps) {
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const { login, verifyOTP } = useAuth();

  const sendMagicLink = async () => {
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setLoading(true);
    setMessage(null);
    setShowOTPInput(false);
    setOtpCode('');
    setOtpError(null);

    try {
      const result = await login(email, next);

      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        onLinkSent?.(true);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch {
      setMessage({
        type: 'error',
        text: 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();
    sendMagicLink();
  };

  const handleOTPSubmit = async () => {
    setOtpLoading(true);
    setOtpError(null);

    try {
      const result = await verifyOTP(email, otpCode);

      if (!result.success) {
        setOtpError(result.message || 'Invalid code');
      }
    } catch {
      setOtpError('Failed to verify code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  if (message?.type === 'success') {
    return (
      <div className="space-y-6 mt-6">
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground">
            Check your email
          </h2>
          <p className="text-sm text-muted-foreground">
            We&apos;ve sent you a temporary login link.
            <br />
            Please check your inbox at{' '}
            <span className="font-medium text-foreground">{email}</span>.
          </p>
        </div>

        {!showOTPInput && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowOTPInput(true)}
            className="w-full flex items-center justify-center"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            Enter code manually
          </Button>
        )}

        {showOTPInput && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              Enter the 6-digit code from your email
            </p>
            <Input
              value={otpCode}
              onChange={(e) =>
                setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              placeholder="000000"
              maxLength={6}
              className="text-center text-2xl tracking-[0.3em] font-mono"
              disabled={otpLoading}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && otpCode.length === 6) {
                  e.preventDefault();
                  handleOTPSubmit();
                }
              }}
            />
            {otpError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{otpError}</AlertDescription>
              </Alert>
            )}
            <Button
              type="button"
              onClick={handleOTPSubmit}
              disabled={otpCode.length !== 6 || otpLoading}
              className="w-full flex items-center justify-center"
            >
              {otpLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <KeyRound className="mr-2 h-4 w-4" />
                  Verify code
                </>
              )}
            </Button>
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setMessage(null);
            setShowOTPInput(false);
            setOtpCode('');
            setOtpError(null);
            onLinkSent?.(false);
          }}
          className="w-full flex items-center justify-center"
        >
          <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
          Back to login
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-6">
      {/* Divider */}
      <div className="relative py-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-border" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-card text-gray-500 dark:text-muted-foreground">
            Or continue with email
          </span>
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-5">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 dark:text-foreground"
          >
            Email address
          </label>
          <div className="mt-1 relative">
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="pl-10"
              disabled={loading}
            />
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-muted-foreground" />
          </div>
        </div>

        {message?.type === 'error' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          variant="outline"
          className="w-full flex items-center justify-center"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending login link...
            </>
          ) : (
            <>
              Send login link
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </form>
      <div className="flex items-center justify-center gap-4 text-xs text-gray-500 dark:text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <svg
            className="w-3.5 h-3.5 text-green-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>Passwordless</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg
            className="w-3.5 h-3.5 text-green-600"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span>Secure</span>
        </div>
      </div>
    </div>
  );
}
